import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { AreaIcon } from '@/src/renderer/components/icons/AreaIcon'
import { useReminderSchedule } from '@/src/renderer/hooks/useReminderSchedule'
import type { Tab } from '@/src/renderer/hooks/multiWindow'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { willShowDoneMoment } from '@/src/renderer/lib/doneMoment'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import type { ReminderAction } from '@/src/shared/types/reminders'
import { setPendingTaskJump } from '@/src/renderer/lib/taskJump/pendingTaskJump'
import { noticeOf } from '../utils/reminderNotice'
import { useTaskOperations } from './useTaskOperations'

/**
 * **Den Plan der Erinnerungen aktuell halten** — beschriftet an die Zustellung. Jedes Hauptfenster tut das
 * (über `useReminderDelivery`), und die Schnelleingabe, solange kein Hauptfenster lebt (#234, macOS): Sonst
 * bekäme eine dort angelegte Aufgabe mit Uhrzeit keine Erinnerung. Gibt die Projekte zurück.
 */
export function useReminderPlan() {
  const { t, i18n } = useTranslation()
  const source = useDataSource()
  const delivery = getPlatformAPI().reminders
  const schedule = useReminderSchedule()
  const projects = source.useProjects()

  useEffect(() => {
    if (!delivery || !schedule || !projects) return
    const names = new Map(projects.map(p => [p.id, p.name]))
    delivery.setPlan({
      notices: schedule.entries.map(entry => noticeOf(entry, entry.projectId ? names.get(entry.projectId) ?? null : null, t, i18n.language)),
      openTaskIds: schedule.openTaskIds,
    })
  }, [delivery, schedule, projects, t, i18n.language])

  return projects
}

/**
 * **Erinnerungen zustellen** (Plan task-reminders, Etappe 3) — in jedem Hauptfenster, in
 * `MainLayout`. Schickt der Zustellung (`PlatformAPI.reminders`) den beschrifteten Plan und führt
 * aus, was in einer Mitteilung angeklickt wurde:
 *
 * - **Klick**: zur Aufgabe in ihrem Projekt (derselbe Sprung wie „Im Projekt zeigen“). Eine
 *   Aufgabe ohne Projekt hat kein Ziel — dann kommt nur das Fenster nach vorn.
 * - **Erledigen**: derselbe Weg wie der Haken in der Liste — die Wiederkehr legt ihren Nachfolger
 *   an, eine Belohnung bringt den Geschafft-Moment; dafür holt die Zustellung das Fenster nach
 *   vorn (Entscheidung 9). Ohne Belohnung bleibt es still.
 * - **Später**: `snoozedUntil` an der Erinnerung — synchronisiert, andere Geräte schlummern mit.
 */
export function useReminderDelivery({ openTab, primary = true }: {
  openTab: (tab: Tab) => void
  /**
   * Nur das erste Hauptfenster führt Aktionen aus — abgerissene Fenster schicken bloß den Plan.
   * Der Plan kommt aus JEDEM: Ist das erste zu (macOS), hielte sonst niemand ihn aktuell.
   */
  primary?: boolean
}): void {
  const { t } = useTranslation()
  const source = useDataSource()
  const delivery = getPlatformAPI().reminders
  const projects = useReminderPlan()
  const { handleToggleComplete } = useTaskOperations({ t })

  /*
    Die Aktionen laufen über einen Ref: Das Abo unten soll einmal stehen, nicht bei jeder neuen
    Projektliste ab- und anmelden — dazwischen könnte ein „Es warten Aktionen“ verloren gehen.
  */
  const perform = useRef<(action: ReminderAction) => Promise<void>>(async () => {})
  perform.current = async (action) => {
    if (action.kind === 'snooze') {
      await source.updateReminder(action.reminderId, { snoozedUntil: new Date(action.until) })
      return
    }
    if (!action.taskId) return
    const task = await source.getTask(action.taskId)
    if (!task || task.deletedAt) return
    if (action.kind === 'complete') {
      // Schon erledigt (anderes Gerät, zweiter Klick): Der Haken ist sein eigenes Gegenteil — nicht zurücknehmen.
      if (task.status === 'completed') return
      await handleToggleComplete(new Set([task.id]))
      if (willShowDoneMoment(task, 'list', 1)) delivery?.showWindow()
      return
    }
    if (!task.projectId) return
    const project = projects?.find(p => p.id === task.projectId)
    setPendingTaskJump(task.projectId, task.id)
    openTab({
      id: `${task.lifeSpace}-project-${task.projectId}-${Date.now()}`,
      path: `/home/${task.lifeSpace}/project/${task.projectId}`,
      title: project?.name ?? '',
      icon: AreaIcon as unknown as LucideIcon,
      lifeSpace: task.lifeSpace,
      isDirty: false,
      isPinned: false,
    })
  }

  useEffect(() => {
    if (!delivery || !primary) return
    let alive = true
    const drain = async () => {
      for (const action of await delivery.takeActions()) {
        if (alive) await perform.current(action)
      }
    }
    // Beim Start abholen, was schon wartet — das Fenster kann gerade FÜR diese Aktion aufgegangen sein.
    void drain()
    const unsubscribe = delivery.onActionsReady(() => { void drain() })
    return () => {
      alive = false
      unsubscribe()
    }
  }, [delivery, primary])
}
